'use client'

import { useEffect } from 'react'
import { recoverFromBoundaryError } from '@/lib/errors/error-boundary-recovery'

/**
 * Route-segment error boundary. Forwards the thrown error into Datadog
 * RUM (when initialized) and renders a neutral fallback with a `Try
 * again` button that calls `reset()` to re-render the segment.
 *
 * A stale-deploy `ChunkLoadError` is recovered with one hard reload
 * instead, because `reset()` would only re-request the missing chunk.
 *
 * Kept design-system-light so it works inside any segment of the app.
 */
export default function AppError({
  error,
  reset,
}: {
  error: Error & { digest?: string }
  reset: () => void
}) {
  useEffect(() => {
    recoverFromBoundaryError(error)
  }, [error])

  return (
    <div className="flex min-h-[60vh] flex-col items-center justify-center gap-4 p-8 text-center">
      <h2 className="font-semibold text-xl">Something went wrong</h2>
      <p className="text-muted-foreground text-sm">
        The error has been reported.
      </p>
      <button
        type="button"
        onClick={reset}
        className="rounded-lg border px-4 py-2 font-medium text-sm hover:bg-muted"
      >
        Try again
      </button>
    </div>
  )
}
